import { useState } from "react";
import { KeyRound } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampPuces, ChampSelection, ChampTexte } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import {
  etatAcces,
  niveauAcces,
  nomPersonne,
  OPTIONS_NIVEAU_ACCES,
  periode,
} from "@/lib/libelles";
import {
  api,
  type Collegue,
  type Habilitation,
  type ModificationHabilitation,
  type NiveauAcces,
} from "@/lib/api";

/**
 * Les habilitations d'une commande : qui d'autre la voit, ou y agit (ADR-0032).
 * L'écran dit les mots de LAB : « droit de niveau 2 », et « autorisé » pour son
 * bénéficiaire (LOT 1 quater, décision 7) ; le code dit habilitation.
 *
 * Une habilitation ouvre CETTE commande à un collègue, en consultation seule ou
 * avec intervention ; elle n'étend jamais ce que son profil lui permet de faire.
 * Le bouton « Accorder » n'apparaît que si la fiche dit `habilitable`, chaque
 * « Révoquer » que si sa ligne dit `revocable`, chaque « Modifier » — niveau et
 * date de fin, rien d'autre (ADR-0043) — que si elle dit `modifiable` : masquer
 * n'est pas autoriser, le serveur refuse quoi qu'il en soit, et son message
 * remonte tel quel.
 */
export function Habilitations({
  commandeId,
  habilitable,
}: {
  commandeId: string;
  habilitable: boolean;
}) {
  const { donnees, erreur, enCours, recharger } = useChargement<Habilitation[]>(
    (signal) => api.habilitations(commandeId, signal),
    [commandeId],
  );
  const [ajout, setAjout] = useState(false);
  const [action, setAction] = useState<string | null>(null);

  async function agir(travail: () => Promise<unknown>) {
    setAction(null);
    try {
      await travail();
      recharger();
      return true;
    } catch (cause) {
      setAction(messageDe(cause));
      return false;
    }
  }

  const lignes = donnees ?? [];
  return (
    <Carte>
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-encre">Droits de niveau 2</h2>
        {habilitable && !ajout ? (
          <Bouton taille="compact" variante="discret" onClick={() => setAjout(true)}>
            <KeyRound className="h-3.5 w-3.5" aria-hidden />
            Accorder
          </Bouton>
        ) : null}
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-3">
        {action ? <Erreur message={action} /> : null}
        {ajout ? (
          <FormulaireHabilitation
            onAnnuler={() => setAjout(false)}
            onValider={async (saisie) => {
              if (await agir(() => api.habiliter({ ...saisie, commande_client_id: commandeId }))) {
                setAjout(false);
              }
            }}
          />
        ) : null}
        {erreur ? <Erreur message={erreur} onReessayer={recharger} /> : null}
        {enCours ? <Chargement message="Chargement des droits de niveau 2…" /> : null}
        {!enCours && lignes.length === 0 ? (
          <p className="text-sm text-encre-douce">
            Aucun : seuls le propriétaire et les profils qui voient tout accèdent à cette
            commande.
          </p>
        ) : null}
        {lignes.length > 0 ? (
          <ul className="divide-y divide-filet">
            {lignes.map((ligne) => (
              <LigneHabilitation
                key={ligne.id}
                habilitation={ligne}
                onModifier={(modification) =>
                  agir(() => api.modifierHabilitation(ligne.id, modification))
                }
                onRevoquer={(motif) => agir(() => api.revoquerHabilitation(ligne.id, motif))}
              />
            ))}
          </ul>
        ) : null}
      </CorpsCarte>
    </Carte>
  );
}

function LigneHabilitation({
  habilitation,
  onModifier,
  onRevoquer,
}: {
  habilitation: Habilitation;
  onModifier: (modification: ModificationHabilitation) => Promise<boolean>;
  onRevoquer: (motif: string | null) => Promise<boolean>;
}) {
  const [geste, setGeste] = useState<"modification" | "revocation" | null>(null);
  const [motif, setMotif] = useState("");
  const etat = etatAcces(habilitation);
  return (
    <li className="flex flex-col gap-2 py-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-encre">{habilitation.beneficiaire_nom}</p>
          <p className="text-encre-douce">
            {niveauAcces(habilitation.niveau)}, {periode(habilitation.du, habilitation.au)}
          </p>
          {habilitation.motif ? (
            <p className="text-encre-douce">Motif : {habilitation.motif}</p>
          ) : null}
          {habilitation.motif_revocation ? (
            <p className="text-encre-douce">Révoqué : {habilitation.motif_revocation}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Etiquette ton={habilitation.en_cours ? "actif" : "neutre"}>{etat}</Etiquette>
          {habilitation.modifiable && !geste ? (
            <Bouton taille="compact" variante="discret" onClick={() => setGeste("modification")}>
              Modifier
            </Bouton>
          ) : null}
          {habilitation.revocable && !geste ? (
            <Bouton taille="compact" variante="discret" onClick={() => setGeste("revocation")}>
              Révoquer
            </Bouton>
          ) : null}
        </div>
      </div>
      {geste === "modification" ? (
        <FormulaireModification
          habilitation={habilitation}
          onAnnuler={() => setGeste(null)}
          onValider={async (modification) => {
            // Fermé seulement si le serveur accepte : un refus garde la saisie.
            if (await onModifier(modification)) setGeste(null);
          }}
        />
      ) : null}
      {geste === "revocation" ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            // Fermé seulement si le serveur accepte : un refus garde le motif saisi.
            if (await onRevoquer(motif.trim() || null)) setGeste(null);
          }}
        >
          <Champ
            libelle="Motif de la révocation (facultatif)"
            value={motif}
            maxLength={1000}
            onChange={(e) => setMotif(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Bouton type="submit" taille="compact" variante="principal">
              Confirmer la révocation
            </Bouton>
            <Bouton
              type="button"
              taille="compact"
              variante="discret"
              onClick={() => setGeste(null)}
            >
              Annuler
            </Bouton>
          </div>
        </form>
      ) : null}
    </li>
  );
}

/** Le niveau et la date de fin ; le bénéficiaire et le début ne changent pas. */
function FormulaireModification({
  habilitation,
  onValider,
  onAnnuler,
}: {
  habilitation: Habilitation;
  onValider: (modification: ModificationHabilitation) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [niveau, setNiveau] = useState<NiveauAcces>(habilitation.niveau);
  const [au, setAu] = useState(habilitation.au ?? "");
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          await onValider({ niveau, au: au || null, motif: motif.trim() || null });
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <ChampPuces
        libelle="Niveau"
        options={OPTIONS_NIVEAU_ACCES}
        valeur={niveau}
        onChanger={(valeur) => setNiveau(valeur as NiveauAcces)}
      />
      <Champ
        libelle="Jusqu'au"
        type="date"
        aide="Vide : sans fin, jusqu'à révocation."
        value={au}
        onChange={(e) => setAu(e.target.value)}
      />
      <Champ
        libelle="Motif de la modification (facultatif)"
        value={motif}
        maxLength={1000}
        onChange={(e) => setMotif(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" taille="compact" variante="principal" disabled={envoi}>
          {envoi ? "Enregistrement…" : "Enregistrer la modification"}
        </Bouton>
        <Bouton
          type="button"
          taille="compact"
          variante="discret"
          onClick={onAnnuler}
          disabled={envoi}
        >
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

type SaisieHabilitation = {
  beneficiaire_id: string;
  niveau: NiveauAcces;
  du: string | null;
  au: string | null;
  motif: string | null;
};

function FormulaireHabilitation({
  onValider,
  onAnnuler,
}: {
  onValider: (saisie: SaisieHabilitation) => Promise<void>;
  onAnnuler: () => void;
}) {
  // Les seuls que le serveur accepterait : ni livreurs ni poseurs (ADR-0032).
  const collegues = useChargement<Collegue[]>((signal) => api.admissibles(signal), []);
  const [beneficiaire, setBeneficiaire] = useState("");
  const [niveau, setNiveau] = useState<NiveauAcces>("CONSULTATION");
  const [du, setDu] = useState("");
  const [au, setAu] = useState("");
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="flex flex-col gap-4 border-b border-filet pb-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!beneficiaire) return;
        setEnvoi(true);
        try {
          await onValider({
            beneficiaire_id: beneficiaire,
            niveau,
            du: du || null,
            au: au || null,
            motif: motif.trim() || null,
          });
        } finally {
          setEnvoi(false);
        }
      }}
    >
      {collegues.enCours ? (
        <Chargement message="Chargement des collègues…" />
      ) : (
        <ChampSelection
          libelle="Autorisé"
          required
          options={[
            { valeur: "", libelle: "— Choisir —" },
            ...(collegues.donnees ?? []).map((collegue) => ({
              valeur: collegue.id,
              libelle: nomPersonne(collegue),
            })),
          ]}
          value={beneficiaire}
          onChange={(e) => setBeneficiaire(e.target.value)}
        />
      )}
      <ChampPuces
        libelle="Niveau"
        options={OPTIONS_NIVEAU_ACCES}
        valeur={niveau}
        onChanger={(valeur) => setNiveau(valeur as NiveauAcces)}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Champ
          libelle="À partir du"
          type="date"
          aide="Vide : aujourd'hui."
          value={du}
          onChange={(e) => setDu(e.target.value)}
        />
        <Champ
          libelle="Jusqu'au"
          type="date"
          aide="Vide : sans fin, jusqu'à révocation."
          value={au}
          onChange={(e) => setAu(e.target.value)}
        />
      </div>
      <ChampTexte
        libelle="Motif (facultatif)"
        value={motif}
        maxLength={1000}
        onChange={(e) => setMotif(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi || !beneficiaire}>
          {envoi ? "Enregistrement…" : "Accorder le droit de niveau 2"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
